(() => {
  if (customElements.get('ah-root-motion')) return;
  class RootMotion extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.video = this.querySelector('video');
      this.stage = this.querySelector('.ah-root-motion__stage');
      this.control = this.querySelector('button');
      this.controlText = this.querySelector('[data-motion-control-text]');
      if (!this.video || !this.stage || !this.control) return;
      this.bound = true;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.visible = false;
      this.userPaused = false;
      this.manual = false;
      this.failed = false;
      this.blocked = false;
      this.playPending = false;
      this.control.hidden = !(this.dataset.desktopSrc || this.dataset.mobileSrc);
      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.loop = true;
      this.video.controls = false;
      this.control.addEventListener('click', () => {
        if (!this.video.paused) {
          this.userPaused = true;
        } else {
          this.manual = true;
          this.userPaused = false;
          this.blocked = false;
          if (this.failed) {
            this.failed = false;
            this.video.removeAttribute('src');
          }
        }
        this.sync();
      }, options);
      this.video.addEventListener('loadeddata', () => {
        this.classList.add('is-ready');
        this.sync();
      }, options);
      this.video.addEventListener('playing', () => {
        this.classList.add('is-ready');
        this.updateControl();
      }, options);
      this.video.addEventListener('pause', () => this.updateControl(), options);
      this.video.addEventListener('timeupdate', () => {
        this.classList.toggle('has-actives', this.video.currentTime >= 3 && this.video.currentTime < 7.5);
      }, options);
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.classList.remove('is-ready', 'has-actives');
        this.updateControl();
      }, options);
      const retry = () => {
        this.blocked = false;
        this.refreshVisibility();
      };
      document.addEventListener('visibilitychange', retry, options);
      window.addEventListener('pageshow', retry, options);
      document.addEventListener('pointerdown', () => {
        if (this.blocked) retry();
      }, { ...options, passive: true });
      this.reduced.addEventListener('change', () => {
        this.manual = false;
        retry();
      }, options);
      if ('IntersectionObserver' in window) {
        // Fetch only near this section, keeping the first product image first.
        this.warmObserver = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && !this.reduced.matches && !navigator.connection?.saveData) this.prepareVideo();
        }, { rootMargin: '900px 0px', threshold: 0 });
        this.warmObserver.observe(this.stage);
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.blocked = false;
          this.sync();
        }, { threshold: 0 });
        this.observer.observe(this.stage);
      } else {
        const refresh = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.refreshVisibility();
          });
        };
        window.addEventListener('scroll', refresh, { ...options, passive: true });
        window.addEventListener('resize', refresh, options);
      }
      this.refreshVisibility();
      this.updateControl();
    }
    refreshVisibility() {
      const rect = this.stage.getBoundingClientRect();
      this.visible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (rect.bottom > -900 && rect.top < window.innerHeight + 900 && !document.hidden && !this.reduced.matches && !navigator.connection?.saveData) this.prepareVideo();
      this.sync();
    }
    wantsPlay() {
      return this.isConnected && this.visible && !document.hidden && !this.userPaused && !this.failed && !this.blocked && (this.manual || !this.reduced.matches);
    }
    prepareVideo() {
      if (this.video.hasAttribute('src')) return true;
      const small = window.matchMedia('(max-width: 749px)').matches;
      const source = small ? (this.dataset.mobileSrc || this.dataset.desktopSrc) : (this.dataset.desktopSrc || this.dataset.mobileSrc);
      if (!source) return false;
      this.video.preload = 'auto';
      this.video.src = source;
      this.video.load();
      this.warmObserver?.disconnect();
      return true;
    }
    sync() {
      const play = this.wantsPlay();
      this.video.autoplay = play;
      if (play) this.playVideo();
      else this.video.pause();
    }
    playVideo() {
      if (!this.prepareVideo() || this.playPending || !this.video.paused) return;
      this.playPending = true;
      this.video.muted = true;
      this.video.play().then(() => {
        if (!this.wantsPlay()) this.video.pause();
      }).catch(error => {
        if (error.name === 'NotAllowedError') this.blocked = true;
        this.updateControl();
      }).finally(() => { this.playPending = false; });
    }
    updateControl() {
      const playing = !this.video.paused && !this.failed;
      const label = this.failed ? this.dataset.retryLabel : (playing ? this.dataset.pauseLabel : this.dataset.playLabel);
      this.classList.toggle('is-playing', playing);
      this.control.setAttribute('aria-label', label);
      if (this.controlText) this.controlText.textContent = label;
    }
    disconnectedCallback() {
      this.video?.pause();
      this.observer?.disconnect();
      this.warmObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.bound = false;
    }
  }
  customElements.define('ah-root-motion', RootMotion);
})();
